/**
 * A modal window is a graphical control element subordinate to an application&#x27;s main window. It creates a mode that disables the main window but keeps it visible, with the dialog window as a child window in front of it
 *
 * @module views/Layouts/OverlayBottomSheet
 */
import './OverlayBottomSheet.scss';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { FocusScope } from '@react-aria/focus';
import PropTypes from 'prop-types';
import { BottomSheet as PalBottomSheet } from 'web-palette/dist/components/BottomSheet/BottomSheet';
import { STYLES as PALETTE_STYLES } from 'web-palette/dist/util/constants/styles';

import Button from '@ulta/core/components/Button/Button';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { ARIA_LIVE_LOCATION, AriaLiveRegion } from '@ulta/core/providers/AriaLiveRegionProvider/AriaLiveRegionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import constants from '@ulta/core/utils/constants/constants';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { isFunction } from '@ulta/core/utils/types/types';

import * as utils from './OverlayBottomSheet';

/**
* Represents a Overlay BottomSheet component
* @type {React.FC<OverlayBottomSheetProps>}
*/
const OverlayBottomSheet = forwardRef( ( props, ref ) => {
  const {
    ariaLabel,
    children,
    closeAccessibilityLabel = defaultProps.closeAccessibilityLabel,
    customClassName,
    disableClose,
    subtitle,
    title,
    ariaLabelledby, // new palette props
    supportText, // new palette props
    footerActions, // new palette props
    variant = 'default', // new palette props. 'default' has the header and footer, 'custom' sends children to a blank bottom sheet,
    heightPercentageOverride
  } = props;

  const { closeOverlay } = useOverlay();
  const [isOpen, buttonRef] = utils.useCustomOverlay( props, ref );
  const showCloseButton = !disableClose;

  if( isServer() ){
    return null;
  }

  const formattedCloseActionElement = ( showCloseButton &&
    <>
      <AriaLiveRegion location={ ARIA_LIVE_LOCATION.Overlay } />
      <Button
        variant='unstyled'
        className={ PALETTE_STYLES.OVERLAY.HEADER_CLOSE_BUTTON }
        ariaLabel={ closeAccessibilityLabel }
        onClick={ () => closeOverlay( { userCancelled: true } ) }
        ref={ buttonRef }
        iconImage={ 'X' }
        iconSize={ 'xl' }
        icon={ true }
        ariaHiddenIcon={ true }
      />
    </>
  );

  const component =  (
    <>
      <div className='OverlayBottomSheet'>
        <FocusScope contain={ true }>
          <PalBottomSheet
            externalCloseActionElement={ formattedCloseActionElement }
            panelRef={ ref }
            ariaLabel={ ariaLabel }
            ariaLabelledBy={ ariaLabelledby }
            isOpen={ isOpen }
            className={ customClassName }
            title={ title?.text }
            titleTag={ title?.htmlTag }
            supportText={ supportText }
            variant={ variant }
            description={ subtitle?.text }
            externalFooterChildrenElements={ footerActions }
            heightPercentageOverride={ heightPercentageOverride }
          >
            { children }
          </PalBottomSheet>
        </FocusScope>
      </div>
    </>
  );

  return createPortal( component, document.body );
} );

/**
 * Manages state for a custom Overlay and polyfills the div to mimic an HTML dialog
 * @param {object} props - Component props
 * @param {object} ref - Flyout ref
 * @returns {array} Returns array of [isOpen, buttonRef]
 */
export const useCustomOverlay = ( props, ref = {} ) => {
  const { disableClose, onClose } = props;

  const [isOpen, setIsOpen] = useState( false );
  const buttonRef = useRef();

  useEffect( () => {
    // Allow time for DOM to update so the initial element can render off screen, then
    // when we flip this to true it will animate in
    const timeOut = setTimeout( () => {
      setIsOpen( true );
    }, 0 );

    return () => clearTimeout( timeOut );
  }, [] );

  const onOverlayClose = useCallback( ( e, data ) => {
    return utils.composeOnOverlayClose( { disableClose, e, data },
      { onClose, setIsOpen } );
  }, [onClose, setIsOpen, disableClose] );

  useEffect( () => {
    return utils.initializeReferenceClose( { ref }, { onOverlayClose } );
  }, [onOverlayClose, disableClose] );

  // Handle ESC key
  UseKeyPress( constants.ESC_KEY, { current: document }, ( e ) => {
    return utils.composeOnUserKeyPress( { disableClose, e }, { onOverlayClose } );
  } );

  useEffect( () => {
    utils.focusCloseButtonOnOpen( { isOpen, buttonRef } );
  }, [isOpen, buttonRef] );

  return [isOpen, buttonRef];
};
/**
 * @method focusCloseButtonOnOpen
 * @summary Focuses the close button when the overlay opens (for keyboard users).
 * @param {object} data - Object which contains isOpen and buttonRef.
 */
export const focusCloseButtonOnOpen = ( data ) => {
  const { isOpen, buttonRef } = data;
  if( !isOpen ){
    return;
  }
  buttonRef.current?.focus();
};
/**
 * @method initializeReferenceClose
 * @summary function initializes the close function of the reference object
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const initializeReferenceClose = ( data, methods ) => {
  const { ref } = data;
  const { onOverlayClose } = methods;
  if( !ref?.current ){
    return;
  }

  // Polyfill the .close function on the element
  // we only set this once because if overlay content gets replaced we will have lost the og reference
  if( !isFunction( ref?.current?.close ) ){

    ref.current.close = onOverlayClose;
  }
};

/**
 * @method composeOnOverlayClose
 * @summary function sets the open and closng state
 * @param {object} overlayData - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnOverlayClose = ( overlayData, methods ) => {
  const { disableClose, e, data } = overlayData;
  const { onClose, setIsOpen } = methods;
  // if disableClose is true do not close.
  if( disableClose ){
    return;
  }
  const timeOut = setTimeout( () => {
    onClose( e, data );
    setIsOpen( false );
  }, 0 );

  return () => clearTimeout( timeOut );
};

/**
 * @method composeOnUserKeyPress
 * @summary function closes the overlay on escape key press
 * @param {object} data - Object which contains data.
 * @param {object} methods - an object containing methods to be passed into the function.
 */
export const composeOnUserKeyPress = ( data, methods ) => {
  const { disableClose, e } = data;
  const { onOverlayClose } = methods;
  if( disableClose ){
    return;
  }
  return onOverlayClose( e, { userCancelled: true } );
};

/**
  * Property type definitions
  * @typedef OverlayBottomSheetProps
  * @type {object}
  * @property {string} ariaLabel - Sets aria label in the bottomsheet
  * @property {string} ariaLabelledby - Sets aria labelled by in the pallet bottomsheet
  * @property {string} closeAccessibilityLabel - Sets close accessibility label
  * @property {string} customClassName - Sets close accessibility label
  * @property {boolean} disableClose - is the close button disabled
  * @property {object} footerActions - sets the externalFooterChildrenElements property of pallet bottomsheet
  * @property {boolean} isOpen - Sets the isOpen on the pallet bottomsheet
  * @property {function} onClose - Sets the onClose event on the dialog element
  * @property {object} subtitle - Sets the subtitle for bottom sheet
  * @property {string} supportText - Sets the supportText for pallet bottomsheet
  * @property {object} title - Sets the title and htmlTag for bottom sheet title
  * @property {string} variant - Sets the variant of pallet bottom sheet. 'default' has the header and footer, 'custom' sends children to a blank bottom sheet
  */

/**
* @type {OverlayBottomSheetProps}
*/
export const propTypes = {
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,
  closeAccessibilityLabel: PropTypes.string,
  customClassName: PropTypes.string,
  disableClose: PropTypes.bool,
  footerActions: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  subtitle: PropTypes.object,
  supportText: PropTypes.string,
  title: PropTypes.object,
  variant: PropTypes.string
};

/**
* Default values for passed properties
* @type {object}
* @property {string} closeAccessibilityLabel - Sets close accessibility label
* @property {string} variant - Sets the variant of pallet bottom sheet. 'default' has the header and footer, 'custom' sends children to a blank bottom sheet
*/

export const defaultProps = {
  closeAccessibilityLabel: 'Close',
  variant: 'default'
};

OverlayBottomSheet.propTypes = propTypes;
OverlayBottomSheet.defaultProps = defaultProps;
OverlayBottomSheet.displayName = 'BottomSheet';

export default OverlayBottomSheet;
